import {
  CreditCard,
  FloppyDisk,
  PauseCircle,
  PlayCircle,
} from "@phosphor-icons/react";
import { useMutation, useQuery } from "convex/react";
import { useRef, useState } from "react";

import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import type { BusinessMembership, CustomerAccountOverview } from "./types";
import { WorkspaceSetupDetail } from "../workspace/WorkspaceSetupDetail";

type PlanKey = CustomerAccountOverview["plan"]["planKey"];

const plans: PlanKey[] = ["STARTER", "BUSINESS", "PRO"];

export function CustomerAccountPage({
  business,
  embedded = false,
  onSignOut,
}: {
  business: BusinessMembership;
  embedded?: boolean;
  onSignOut: () => void;
}) {
  const businessId = business.businessId as Id<"businesses">;
  const overview = useQuery(api.customerAccount.getOverview, {
    businessId,
  }) as CustomerAccountOverview | undefined;

  const panel = <AccountPanel businessId={businessId} overview={overview} />;

  if (embedded) {
    return (
      <WorkspaceSetupDetail
        description="Review account settings, plan state and visible usage for this business."
        eyebrow="Account settings"
        title="Business account"
      >
        {panel}
      </WorkspaceSetupDetail>
    );
  }

  return (
    <main className="foundation-shell business-setup-shell">
      <section aria-live="polite" className="status-panel">
        <span className="status-label">Customer account</span>
        <strong>{business.name}</strong>
        <span>{business.role} workspace</span>
        <button onClick={onSignOut} type="button">
          Sign out
        </button>
      </section>
      {panel}
    </main>
  );
}

function AccountPanel({
  businessId,
  overview,
}: {
  businessId: Id<"businesses">;
  overview: CustomerAccountOverview | undefined;
}) {
  const owner = overview?.role === "OWNER";

  return (
    <section className="business-setup-panel customer-account-panel">
      <div className="business-setup-toolbar">
        <div>
          <span className="status-label">Plan</span>
          <strong>{overview?.plan.planKey ?? "Loading"}</strong>
        </div>
        <div>
          <span className="status-label">Subscription</span>
          <strong>{overview?.subscription.status ?? "Checking"}</strong>
        </div>
      </div>
      <PlanControls businessId={businessId} overview={overview} owner={owner} />
      <SettingsControls
        businessId={businessId}
        overview={overview}
        owner={owner}
      />
      <UsagePanel overview={overview} />
    </section>
  );
}

function PlanControls({
  businessId,
  overview,
  owner,
}: {
  businessId: Id<"businesses">;
  overview: CustomerAccountOverview | undefined;
  owner: boolean;
}) {
  const selectPlan = useMutation(api.customerAccount.selectPlan);
  const cancelSubscription = useMutation(
    api.customerAccount.cancelSubscription,
  );
  const resumeSubscription = useMutation(
    api.customerAccount.resumeSubscription,
  );
  const [feedback, setFeedback] = useState("Ready");
  const [pending, setPending] = useState(false);
  const requestSequence = useRef(0);

  function nextRequestId(prefix: string) {
    requestSequence.current += 1;
    return `${prefix}-${requestSequence.current}`;
  }

  async function choosePlan(planKey: PlanKey) {
    if (!owner || pending) return;
    setPending(true);
    setFeedback("Updating");
    try {
      await selectPlan({
        businessId,
        planKey,
        clientRequestId: nextRequestId(`ui-plan-${planKey.toLowerCase()}`),
      });
      setFeedback("Plan updated");
    } catch {
      setFeedback("Change denied");
    } finally {
      setPending(false);
    }
  }

  async function changeSubscription(action: "cancel" | "resume") {
    if (!owner || pending) return;
    setPending(true);
    setFeedback(action === "cancel" ? "Cancelling" : "Resuming");
    try {
      const mutate =
        action === "cancel" ? cancelSubscription : resumeSubscription;
      await mutate({
        businessId,
        clientRequestId: nextRequestId(`ui-subscription-${action}`),
      });
      setFeedback(action === "cancel" ? "Cancelled" : "Resumed");
    } catch {
      setFeedback("Change denied");
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="customer-account-section">
      <header>
        <CreditCard aria-hidden weight="bold" />
        <div>
          <strong>Plan selection</strong>
          <span>{feedback}</span>
        </div>
      </header>
      <div className="plan-choice-row">
        {plans.map((planKey) => (
          <button
            disabled={!owner || pending || overview?.plan.planKey === planKey}
            key={planKey}
            onClick={() => void choosePlan(planKey)}
            type="button"
          >
            {planKey}
          </button>
        ))}
      </div>
      <div className="plan-choice-row">
        <button
          disabled={
            !owner || pending || overview?.subscription.status === "CANCELLED"
          }
          onClick={() => void changeSubscription("cancel")}
          type="button"
        >
          <PauseCircle aria-hidden weight="bold" />
          Cancel
        </button>
        <button
          disabled={
            !owner || pending || overview?.subscription.status !== "CANCELLED"
          }
          onClick={() => void changeSubscription("resume")}
          type="button"
        >
          <PlayCircle aria-hidden weight="bold" />
          Resume
        </button>
      </div>
    </section>
  );
}

function SettingsControls({
  businessId,
  overview,
  owner,
}: {
  businessId: Id<"businesses">;
  overview: CustomerAccountOverview | undefined;
  owner: boolean;
}) {
  const updateSettings = useMutation(api.customerAccount.updateAccountSettings);
  const [billingContactName, setBillingContactName] = useState("");
  const [billingContactEmail, setBillingContactEmail] = useState("");
  const [accountReference, setAccountReference] = useState("");
  const [feedback, setFeedback] = useState("Ready");
  const requestSequence = useRef(0);

  function nextRequestId() {
    requestSequence.current += 1;
    return `ui-settings-${requestSequence.current}`;
  }

  async function saveSettings() {
    if (!owner) return;
    setFeedback("Saving");
    try {
      await updateSettings({
        businessId,
        billingContactName,
        billingContactEmail,
        accountReference,
        clientRequestId: nextRequestId(),
      });
      setFeedback("Saved");
    } catch {
      setFeedback("Save denied");
    }
  }

  return (
    <section className="customer-account-section">
      <header>
        <FloppyDisk aria-hidden weight="bold" />
        <div>
          <strong>Account settings</strong>
          <span>{feedback}</span>
        </div>
      </header>
      <div className="account-settings-grid">
        <input
          disabled={!owner}
          onChange={(event) => setBillingContactName(event.target.value)}
          placeholder={
            overview?.accountSettings.billingContactName ?? "Billing contact"
          }
          value={billingContactName}
        />
        <input
          disabled={!owner}
          onChange={(event) => setBillingContactEmail(event.target.value)}
          placeholder={
            overview?.accountSettings.billingContactEmail ??
            "billing@example.com"
          }
          value={billingContactEmail}
        />
        <input
          disabled={!owner}
          onChange={(event) => setAccountReference(event.target.value)}
          placeholder={
            overview?.accountSettings.accountReference ?? "Account reference"
          }
          value={accountReference}
        />
        <button
          disabled={!owner}
          onClick={() => void saveSettings()}
          type="button"
        >
          Save
        </button>
      </div>
    </section>
  );
}

function UsagePanel({
  overview,
}: {
  overview: CustomerAccountOverview | undefined;
}) {
  return (
    <section className="customer-account-section">
      <header>
        <strong>Usage visibility</strong>
        <span>{overview?.usage.planKey ?? "Loading"}</span>
      </header>
      <div className="business-setup-metrics">
        {(overview?.usage.limits ?? []).map((limit) => (
          <article className="business-setup-metric" key={limit.metric}>
            <span>{limit.metric}</span>
            <strong>{usageValue(overview, limit.metric)}</strong>
            <small>
              {limit.limit === null ? "Unlimited" : `Limit ${limit.limit}`}
            </small>
          </article>
        ))}
      </div>
    </section>
  );
}

function usageValue(
  overview: CustomerAccountOverview | undefined,
  metric: CustomerAccountOverview["usage"]["limits"][number]["metric"],
) {
  const counter = overview?.usage.counters.find(
    (entry) => entry.saasUsageMetric === metric,
  );

  return counter?.quantity ?? 0;
}
